import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useTranslation } from "react-i18next";
import { Loader2, Plus } from "lucide-react";
import { api } from "@/lib/api";
import type { InstanceEmployee, InstanceEmployeeSyncStatus } from "@/types/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";

function syncVariant(
  status: InstanceEmployeeSyncStatus
): "success" | "muted" | "destructive" | "secondary" {
  if (status === "applied") return "success";
  if (status === "failed") return "destructive";
  if (status === "archived") return "muted";
  return "secondary"; // draft | pending
}

/**
 * "AI Employees" card on the instance page: the centrally-managed bots
 * of this client. Rows link to the full editor; the switch toggles
 * activation (delivered to the instance by the Phase 3 sync).
 */
export function InstanceEmployeesSection({ name }: { name: string }) {
  const { t } = useTranslation("instances");
  const qc = useQueryClient();
  const navigate = useNavigate();

  const { data, isLoading } = useQuery({
    queryKey: ["instance", name, "employees"],
    queryFn: () =>
      api.get<{ employees: InstanceEmployee[] }>(
        `/api/admin/instances/${name}/employees`
      ),
    // The Phase 3 sync flips pending/failed → applied asynchronously (the
    // instance acks within ~1 minute), so a static list goes stale against
    // the detail page (e.g. a "failed vN" badge for a bot that has long
    // been applied). Poll while any bot is in a transitional state.
    refetchInterval: (query) => {
      const employees = query.state.data?.employees ?? [];
      return employees.some(
        (e) => e.syncStatus === "pending" || e.syncStatus === "failed"
      )
        ? 7000
        : false;
    },
  });

  const activationMutation = useMutation({
    mutationFn: ({ id, active }: { id: string; active: boolean }) =>
      api.post<{ employee: InstanceEmployee }>(
        `/api/admin/instances/${name}/employees/${id}/${active ? "activate" : "deactivate"}`
      ),
    onSuccess: (res) => {
      toast.success(
        res.employee.isActive ? t("employees.activated") : t("employees.deactivated")
      );
      qc.invalidateQueries({ queryKey: ["instance", name, "employees"] });
    },
    onError: (err: unknown) => {
      toast.error(err instanceof Error ? err.message : t("employees.updateFailed"));
    },
  });

  const employees = data?.employees ?? [];
  const visible = employees.filter((e) => e.syncStatus !== "archived");

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <CardTitle>{t("employees.title")}</CardTitle>
            <CardDescription>
              {t("employees.description")}
            </CardDescription>
          </div>
          <Button size="sm" asChild>
            <Link to={`/instances/${name}/employees/new`}>
              <Plus className="mr-2 h-4 w-4" /> {t("employees.new")}
            </Link>
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <Skeleton className="h-24 w-full" />
        ) : visible.length === 0 ? (
          <p className="py-4 text-sm text-muted-foreground">
            {t("employees.empty")}
          </p>
        ) : (
          <div className="divide-y">
            {visible.map((e) => (
              <div
                key={e.id}
                className="flex flex-wrap items-center gap-3 py-3"
              >
                <button
                  type="button"
                  className="min-w-0 flex-1 text-left hover:underline"
                  onClick={() => navigate(`/instances/${name}/employees/${e.id}`)}
                >
                  <span className="block truncate font-medium">{e.name}</span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {e.employeeType}
                    {e.sourceTemplateId ? ` · ${t("employees.fromTemplate", { template: e.sourceTemplateId })}` : ""}
                  </span>
                </button>
                <Badge
                  variant={syncVariant(e.syncStatus)}
                  title={e.syncStatus === "failed" ? (e.syncError ?? "") : undefined}
                >
                  {e.syncStatus}
                  {e.syncStatus === "applied" && e.appliedVersion !== null
                    ? ` v${e.appliedVersion}`
                    : ` v${e.version}`}
                </Badge>
                <div className="flex items-center gap-2">
                  {activationMutation.isPending && (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  )}
                  <Switch
                    checked={e.isActive}
                    disabled={activationMutation.isPending}
                    onCheckedChange={(active) =>
                      activationMutation.mutate({ id: e.id, active })
                    }
                    aria-label={t("employees.toggle", { name: e.name })}
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
